import { useDataProvider, useNotify } from "ra-core";
import { Link } from "react-router";

import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { labelOf } from "./gulaConstants";
import { formatDue, isTaskOverdue, type GulaTask } from "./gulaTasks";
import type { Agent } from "./useAgents";
import { agentName } from "./useAgents";

/** One task: done checkbox, what, who it is about, when and who does it. */
export const TaskRow = ({
  task,
  contactName,
  clientId,
  agents,
  onChanged,
}: {
  task: GulaTask;
  contactName?: string;
  clientId?: string | number | null;
  agents: Agent[];
  onChanged: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { taskTypes } = useConfigurationContext();
  const overdue = isTaskOverdue(task.due_date);

  const update = async (data: Partial<GulaTask>, message?: string) => {
    try {
      await dataProvider.update("tasks", {
        id: task.id,
        data,
        previousData: { id: task.id },
      });
      if (message) notify(message, { type: "success" });
      onChanged();
    } catch (error) {
      console.error(error);
      notify("No se ha podido actualizar la tarea", { type: "error" });
    }
  };

  return (
    <div className="flex items-start gap-3 py-2">
      <Checkbox
        className="mt-0.5"
        aria-label="Marcar como hecha"
        checked={!!task.done_date}
        onCheckedChange={() =>
          update({ done_date: new Date().toISOString() }, "Tarea hecha")
        }
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm leading-snug">
          {task.type && task.type !== "none" ? (
            <span className="mr-1 font-medium">
              {labelOf(taskTypes, task.type)}:
            </span>
          ) : null}
          {task.text}
        </span>
        <span className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          {contactName ? (
            clientId != null ? (
              <Link
                to={`/clientes?cliente=${clientId}`}
                className="font-medium text-foreground hover:underline"
              >
                {contactName}
              </Link>
            ) : (
              <span className="font-medium text-foreground">{contactName}</span>
            )
          ) : null}
          <span className={cn(overdue && "font-semibold text-destructive")}>
            {formatDue(task.due_date)}
          </span>
          {task.auto_key ? <span>· automática</span> : null}
        </span>
      </div>
      <select
        aria-label="Agente"
        value={task.sales_id == null ? "" : String(task.sales_id)}
        onChange={(event) =>
          update(
            {
              sales_id: event.target.value ? Number(event.target.value) : null,
            },
            "Tarea reasignada",
          )
        }
        className="h-7 max-w-28 shrink-0 rounded-md border bg-background px-1 text-xs"
      >
        <option value="">Sin asignar</option>
        {agents
          .filter((agent) => !agent.no_work)
          .map((agent) => (
            <option key={agent.id} value={String(agent.id)}>
              {agentName(agent)}
            </option>
          ))}
      </select>
    </div>
  );
};
